import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";
import BrandMark from "@/components/BrandMark";

const OnboardingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("checking"); // checking | active | pending | error
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`${API}/host/subscription/status/${sessionId}`, { headers: await authHeaders() });
        const data = await res.json();
        if (res.ok && data.activated) {
          setPlan(data.profile?.subscription?.plan);
          setState("active");
          setTimeout(() => navigate("/host", { replace: true }), 2200);
          return;
        }
        if (tries >= 8) { setState("pending"); return; }
      } catch {
        if (tries >= 8) { setState("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f1e8] text-zinc-900 px-6" data-testid="onboarding-success-page">
      <div className="max-w-md w-full text-center">
        <BrandMark size="lg" className="justify-center mb-6" />
        {state === "checking" && (
          <div data-testid="success-checking">
            <Loader2 className="w-10 h-10 animate-spin text-zinc-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Confirming your subscription…</h1>
            <p className="text-zinc-500 mt-2 text-sm">Hang tight, this only takes a moment.</p>
          </div>
        )}
        {state === "active" && (
          <div data-testid="success-active">
            <CheckCircle2 className="w-14 h-14 text-[#8bba00] mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-5">You're all set! 🎉</h1>
            <p className="text-zinc-600 mt-2">Your {plan ? plan + " " : ""}plan is active. Taking you to your Host Hub…</p>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="success-pending">
            <Loader2 className="w-10 h-10 text-zinc-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Almost there</h1>
            <p className="text-zinc-500 mt-2 text-sm">Your payment is processing. You can head into the Hub — it'll unlock shortly.</p>
            <Button onClick={() => navigate("/host")} className="mt-5 rounded-full gap-2" data-testid="success-go-hub">
              Go to Host Hub <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        {state === "error" && (
          <div data-testid="success-error">
            <XCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">We couldn't confirm your payment</h1>
            <p className="text-zinc-500 mt-2 text-sm">If you completed checkout, your plan will activate automatically. Otherwise, try again.</p>
            <div className="flex gap-2 justify-center mt-5">
              <Button variant="outline" onClick={() => navigate("/onboarding?step=plan")} className="rounded-full" data-testid="success-retry">Try again</Button>
              <Button onClick={() => navigate("/host")} className="rounded-full" data-testid="success-skip">Go to Hub</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingSuccessPage;
